import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { ProximityService, NearbyUser, ProximitySettings } from '../../services/proximity.service';
import { ChatService } from '../../services/chat.service';
import { WebSocketService } from '../../services/websocket.service';

@Component({
  selector: 'app-proximity',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './proximity.component.html',
  styleUrls: ['./proximity.component.scss']
})
export class ProximityComponent implements OnInit, OnDestroy {
  nearbyUsers: NearbyUser[] = [];
  settings: ProximitySettings = {
    enabled: false,
    radiusMeters: 1000,
    autoJoinChat: false,
    notificationsEnabled: true
  };
  
  isConnected = false;
  isLoading = false;

  private subscriptions = new Subscription();

  constructor(
    private proximityService: ProximityService,
    private chatService: ChatService,
    private wsService: WebSocketService
  ) {}

  ngOnInit(): void {
    this.setupSubscriptions();
    this.requestPermissions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupSubscriptions(): void {
    // Usuarios cercanos
    this.subscriptions.add(
      this.proximityService.nearbyUsers$.subscribe(users => {
        this.nearbyUsers = users;
      })
    );

    // Configuración de proximidad
    this.subscriptions.add(
      this.proximityService.settings$.subscribe(settings => {
        this.settings = settings;
      })
    );

    // Estado de conexión WebSocket
    this.subscriptions.add(
      this.wsService.connectionStatus$.subscribe(connected => {
        this.isConnected = connected;
      })
    );
  }

  private async requestPermissions(): Promise<void> {
    // Solicitar permisos de ubicación
    await this.proximityService.requestLocationPermission();
    
    // Solicitar permisos de notificación
    await this.proximityService.requestNotificationPermission();
  }

  async toggleProximity(): Promise<void> {
    this.isLoading = true;
    
    try {
      if (this.settings.enabled) {
        const success = await this.proximityService.disableProximityNotifications();
        if (!success) {
          alert('Error al deshabilitar proximidad');
        }
      } else {
        const success = await this.proximityService.enableProximityNotifications(this.settings.radiusMeters);
        if (!success) {
          alert('Error al habilitar proximidad');
        }
      }
    } catch (error) {
      console.error('Error toggling proximity:', error);
      alert('Error al cambiar configuración de proximidad');
    } finally {
      this.isLoading = false;
    }
  }

  async joinProximityChat(): Promise<void> {
    this.isLoading = true;
    
    try {
      const success = await this.chatService.joinProximityChat();
      if (success) {
        // Navegar al chat
        window.location.href = '/chat';
      } else {
        alert('Error al unirse al chat de proximidad');
      }
    } catch (error) {
      console.error('Error joining proximity chat:', error);
      alert('Error al unirse al chat');
    } finally {
      this.isLoading = false;
    }
  }

  updateRadius(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newRadius = parseInt(target.value);
    
    if (newRadius >= 100 && newRadius <= 5000) {
      this.proximityService.updateSettings({ radiusMeters: newRadius });
      
      // Si está habilitado, actualizar
      if (this.settings.enabled) {
        this.proximityService.enableProximityNotifications(newRadius);
      }
    }
  }

  formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else {
      return `${(meters / 1000).toFixed(1)}km`;
    }
  }

  formatLastSeen(date: Date): string {
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Ahora';
    if (diffMinutes < 60) return `Hace ${diffMinutes}min`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    
    return 'Hace más de 1 día';
  }

  getUserValidationIcon(level: number): string {
    if (level >= 3) return '🟢'; // Verificado
    if (level >= 2) return '🟡'; // Parcialmente verificado
    return '🔴'; // Sin verificar
  }

  getReputationStars(reputation: number): string {
    const stars = Math.min(5, Math.max(0, Math.floor(reputation / 20)));
    return '⭐'.repeat(stars) + '☆'.repeat(5 - stars);
  }
}